import * as React from 'react';
import TreeView from '@mui/lab/TreeView';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TreeItem from '@mui/lab/TreeItem';
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {CustomCheckbox} from '../CustomCheckbox';

/**
 * CustomTreeView component to render a tree with checkboxes.
  * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 * @param {Object} props - Component props.
 * @param {string} props.tittle - Title to display in the header (translation key).
 * @param {Array} props.data - Array of objects representing the tree structure. Each object should include:
 *                             `id` (identifier), `name` (display name) and optionally `children` (sub-nodes).
 * @param {Function} props.onInputchange - Function triggered when a checkbox state changes.
 * @param {Array} props.isChecked - Array with selected values in the format "id,name".
 * @param {Array} props.checked - Array with currently checked values.
 * @param {Function} props.setChecked - Function to update the checked state.
 * @returns {JSX.Element} Rendered component.
 */
const CustomTreeView = ({ tittle='', data, onInputchange, isChecked, checked, setChecked, className="h-250px" }) => {
    const [isAllChecked, setIsAllChecked] = useState(false);
    const { t } = useTranslation();

    /**
     * Handles the selection or deselection of all tree items.
     *
     * @param {React.ChangeEvent<HTMLInputElement>} e - The event triggered by the "select all" checkbox.
     */
    const handleZoneCheckAll = (e) => {
        setIsAllChecked(e.target.checked);
        if (e.target.checked) {
            const selectAllIds = getAllItemIds(data);
            setChecked(selectAllIds);
        } else {
            setChecked([]);
        }
    };

    /**
     * Recursively retrieves the IDs of all tree items.
     *
     * @param {Array} nodes - Array of tree nodes.
     * @returns {Array<string>} Array of IDs in the format "id,name".
     */
    const getAllItemIds = (nodes) => {
        return nodes.reduce((acc, node) => {
            acc.push(`${node.id},${node.name}`);
            if (node.children && node.children.length > 0) {
                acc = acc.concat(getAllItemIds(node.children));
            }
            return acc;
        }, []);
    };

    /**
     * Recursively renders TreeItem components.
     *
     * @param {Array} nodes - Array of nodes to render.
     * @returns {JSX.Element[]} Array of TreeItem components.
     */
    const renderTreeItems = (nodes) => {
        return nodes.map((node) => (
            <TreeItem
                key={node.id}
                nodeId={String(node.id)}
                label={
                    <React.Fragment>
                        <CustomCheckbox
                            id={`${node.id}`}
                            trans={node.name}
                            name={"selectAll"}
                            checked={isChecked.includes(`${node.id},${node.name}`)}
                            onChange={(event) => {
                                event.stopPropagation();
                                onInputchange({ target: { value: `${node.id},${node.name}`, checked: event.target.checked} });
                            }}
                            marginbutton={true}
                        />
                    </React.Fragment>
                }
            >
                {node.children && node.children.length > 0 && renderTreeItems(node.children)}
            </TreeItem>
        ));
    };

    // Retrieve all IDs for the "select all" checkbox
    const allItemIds = getAllItemIds(data);

    return (
        <>
            <div className='d-flex align-items-center'>
                {tittle !== '' && <p className='heading_tree'>{t(tittle)}</p> }
                <CustomCheckbox id={"cb_ctv_de_select_all"}
                                trans={t("de_/_select_all")}
                                name={"selectAllDays"}
                                checked={checked?.length > 0 && checked?.length === allItemIds?.length}
                                onChange={handleZoneCheckAll}
                                marginbutton={true}
                                className={"ml-2"}
                />
            </div>
            <div className={`overflow-y-auto maxh-412px ${className}`}>
                <TreeView
                    aria-label='file system navigator'
                    defaultCollapseIcon={<ExpandMoreIcon />}
                    defaultExpandIcon={<ChevronRightIcon />}
                >
                    {renderTreeItems(data)}
                </TreeView>
            </div>
        </>
    );
};

export default CustomTreeView;
